import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Col, Container, Div, Image, Input, Row, Text } from '@pinpoint/design-kit';
import { connect } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

import CONSTANTS from 'src/store/constants';
import { authActions } from 'src/store/actions';
import { ErrorText, Button } from 'src/components';

const defaultValues = {
  confirmationCode: '',
  newPassword: '',
  confirmPassword: '',
};

function ChangePassword({ history, username, changePasswordStatus, changePasswordError, changePassword }) {
  const [lockUsername, setLockUsername] = useState(true);
  const { handleSubmit, errors, control, setError, watch } = useForm({
    defaultValues: {
      username,
      ...defaultValues,
    },
  });

  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!username) setLockUsername(false);
  }, [username]);

  const onSubmit = (data) => {
    const { confirmationCode, newPassword, confirmPassword, username: formUsername } = data;
    if (confirmPassword === newPassword) {
      const submitUsername = username || formUsername;
      changePassword(confirmationCode, submitUsername, newPassword);
    } else {
      setError('confirmPassword', 'value', "The passwords don't match");
    }
  };

  useEffect(() => {
    if (changePasswordStatus === CONSTANTS.API.SUCCEEDED) {
      history.push('/login');
    } else if (changePasswordStatus === CONSTANTS.API.FAILED) {
      setErrorMessage(changePasswordError);
    }
  }, [changePasswordStatus, changePasswordError, history]);

  return (
    <Div
      minW="100vw"
      w="100%"
      minH="100vh"
      h="100%"
      d="flex"
      justify="center"
      align="center"
      p={{ x: '1rem', y: '4rem' }}
      pos="relative"
    >
      <Image
        src="/static/login-back.png"
        srcSet="/static/login-back@2x.png 2x,/static/login-back@3x.png 3x,"
        alt="background"
        minW="100%"
        maxW="100%"
        minH="100%"
        maxH="100%"
        pos="absolute"
        top="0"
        style={{
          objectFit: 'cover',
        }}
      />
      <Div
        w="420px"
        d="flex"
        flexDirection="column"
        align="center"
        shadow="5"
        p={{ x: '2rem', t: '3rem', b: '1rem' }}
        rounded="sm"
        bg="black75"
        zIndex="10"
        style={{
          backdropFilter: 'blur(8px)',
        }}
      >
        <Container>
          <Div d="flex" justify="center" align="center">
            <Image src="/static/on-point-login.svg" w="108px" h="78px" />
          </Div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <Row p={{ y: '2rem' }}>
              <Col size={{ xs: '12' }} d="flex" justify="center">
                <Text textSize="subtitle" textColor="gray400">
                  In order to verify your request, we have sent a confirmation code to your email address if it exists
                  in our system. Please enter the confirmation code, your username or email (if not filled in) and
                  change your password.
                </Text>
              </Col>
            </Row>
            <Row p={{ b: errorMessage ? '1rem' : '0rem' }} d="flex" justify="center">
              <Col>
                <Div d="flex" align="center">
                  <ErrorText shake={errorMessage !== ''}>{errorMessage}</ErrorText>
                </Div>
              </Col>
            </Row>
            <Row p={{ y: '1rem' }}>
              <Col size={{ xs: '12' }}>
                <Controller
                  name="username"
                  as={Input}
                  control={control}
                  placeholder="Username or Email"
                  disabled={lockUsername}
                  defaultValue={username || null}
                  type="input"
                  rules={{
                    required: 'Username or email is required',
                  }}
                  rounded="sm"
                  p={{ y: '1em', x: '1.5em' }}
                  h="2.4rem"
                  textSize="input"
                />
              </Col>
            </Row>
            <Row>
              <Col size={{ xs: '12' }}>
                <Text textColor="danger600" textSize="caption">
                  <ErrorMessage errors={errors} name="username" />
                </Text>
              </Col>
            </Row>
            <Row p={{ y: '1rem' }}>
              <Col size={{ xs: '12' }} d="flex" justify="center">
                <Div w="25%" border={{ t: '1px solid' }} borderColor="greenDarkPale" key="spacer" />
              </Col>
            </Row>
            <Row p={{ y: '1rem' }}>
              <Col size={{ xs: '12' }}>
                <Controller
                  name="confirmationCode"
                  as={Input}
                  control={control}
                  placeholder="Confirmation Code"
                  type="input"
                  rules={{
                    required: 'Confirmation code is required',
                  }}
                  onKeyDown={(event) => {
                    if (event.keyCode === 13) {
                      handleSubmit(onSubmit)(event);
                    }
                  }}
                  rounded="sm"
                  p={{ y: '1em', x: '1.5em' }}
                  h="2.4rem"
                  textSize="input"
                />
              </Col>
            </Row>
            <Row>
              <Col size={{ xs: '12' }}>
                <Text textColor="danger600" textSize="caption">
                  <ErrorMessage errors={errors} name="confirmationCode" />
                </Text>
              </Col>
            </Row>
            <Row p={{ y: '1rem' }}>
              <Col size={{ xs: '12' }} d="flex" justify="center">
                <Div w="25%" border={{ t: '1px solid' }} borderColor="greenDarkPale" key="spacer" />
              </Col>
            </Row>
            <Row p={{ y: '1rem' }}>
              <Col size={{ xs: '12' }}>
                <Controller
                  name="newPassword"
                  as={Input}
                  control={control}
                  placeholder="New Password"
                  type="password"
                  rules={{
                    required: 'New password is required',
                    minLength: {
                      value: 10,
                      message: 'Password must contain at least 10 characters',
                    },
                    maxLength: {
                      value: 99,
                      message: 'Password has to be less than 100 characters',
                    },
                    validate: (value) =>
                      (/[a-z]/.test(value) &&
                        /[A-Z]/.test(value) &&
                        /[0-9]/.test(value) &&
                        /[\^$.[\]{}()?"!@#%&/\\,><':;|_~]/.test(value)) ||
                      'Password should contain uppercase and lowercase characters, a number and one of the following special characters [^$.[]{}()?"!@#%&/\\,><\':;|_~]',
                  }}
                  onKeyDown={(event) => {
                    if (event.keyCode === 13) {
                      handleSubmit(onSubmit)(event);
                    }
                  }}
                  rounded="sm"
                  p={{ y: '1em', x: '1.5em' }}
                  h="2.4rem"
                  textSize="input"
                />
              </Col>
            </Row>
            <Row p={{ b: errors.newPassword ? '1rem' : '0rem' }}>
              <Col size={{ xs: '12' }}>
                <Text textColor="danger600" textSize="caption">
                  <ErrorMessage errors={errors} name="newPassword" />
                </Text>
              </Col>
            </Row>
            <Row p={{ b: '1rem' }}>
              <Col size={{ xs: '12' }}>
                <Controller
                  name="confirmPassword"
                  as={Input}
                  control={control}
                  placeholder="Confirm Password"
                  type="password"
                  rules={{
                    required: 'Password is required',
                    validate: (value) => value === watch('newPassword') || "The passwords don't match",
                  }}
                  onKeyDown={(event) => {
                    if (event.keyCode === 13) {
                      handleSubmit(onSubmit)(event);
                    }
                  }}
                  rounded="sm"
                  p={{ y: '1em', x: '1.5em' }}
                  h="2.4rem"
                  textSize="input"
                />
              </Col>
            </Row>
            <Row p={{ b: errors.confirmPassword ? '1rem' : '0rem' }}>
              <Col size={{ xs: '12' }}>
                <Text textColor="danger600" textSize="caption">
                  <ErrorMessage errors={errors} name="confirmPassword" />
                </Text>
              </Col>
            </Row>
            <Row p={{ b: '1rem' }}>
              <Col size={{ xs: '12' }}>
                <Div d="flex" justify="center">
                  <Button
                    buttonStyle="login"
                    onClick={handleSubmit(onSubmit)}
                    onKeyDown={(event) => {
                      event.preventDefault();
                      if (event.keyCode === 13) {
                        handleSubmit(onSubmit)(event);
                      }
                    }}
                    isLoading={changePasswordStatus === CONSTANTS.API.STARTED}
                  >
                    Change Password
                  </Button>
                </Div>
              </Col>
            </Row>
          </form>
          <Div d="flex" justify="space-between" align="center">
            <Div p={{ y: '1rem' }} d="flex" justify="center">
              <Image w="90px" h="2rem" src="/static/powered-by-pinpoint-white.svg" />
            </Div>
            <Div h="1.5rem" border={{ r: '1px solid' }} borderColor="gray400" />
            <Div p={{ y: '1rem' }} d="flex" justify="center">
              <Text
                textDecor="underline"
                textSize="caption"
                textColor="gray400"
                cursor="pointer"
                onClick={() => {
                  window.open('https://pinpointpredictive.com/tos/', '_blank') ||
                    window.location.replace('https://pinpointpredictive.com/tos/');
                }}
              >
                Terms of Service
              </Text>
            </Div>
            <Div h="1.5rem" border={{ r: '1px solid' }} borderColor="gray400" />
            <Div p={{ y: '1rem' }} d="flex" justify="center">
              <Text
                textDecor="underline"
                textSize="caption"
                textColor="gray400"
                cursor="pointer"
                onClick={() => {
                  history.push('/login');
                }}
              >
                Login
              </Text>
            </Div>
          </Div>
        </Container>
      </Div>
    </Div>
  );
}

const mapStateToProps = ({ auth }) => ({
  ...auth,
});

const mapDispatchToProps = (dispatch) => ({
  login: (...params) => dispatch(authActions.login(...params)),
  changePassword: (...params) => dispatch(authActions.changePassword(...params)),
});

ChangePassword.propTypes = {
  username: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  changePasswordStatus: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  changePasswordError: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  changePassword: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(ChangePassword);
